import { Skeleton } from '@/components/ui/skeleton';
import { LeadConcern } from '@/hooks/useConversationsInsights';
import React from 'react';
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from 'recharts';

const PIE_COLORS = [
  '#E7EBF3',
  '#C4C8F7',
  '#9194EF',
  '#5D52FB',
  '#4448D5',
  '#607AE3',
  '#4C6CE0',
  '#385EE0',
  '#214BCA',
  '#3F34D3',
  '#1B399B',
  '#142A70',
  '#0B1161',
  '#023207',
  '#105537',
  '#157147',
  '#1E8C5A',
  '#34A26E',
  '#4EAF83',
  '#69BD98',
  '#31B58B',
  '#1B906B',
  '#31A781',
  '#41D5A6',
  '#6FC7AC',
  '#9AE3CD',
  '#82C9AE',
  '#9AD6C5',
  '#B4E3DD',
  '#DDEFE8',
  '#E6FBF3',
];

interface Props {
  loadingKeyInsights?: boolean;
  leadConcerns?: LeadConcern[];
  agentPitchGaps?: undefined[] | Record<string, any>;
}

/* ═══ map API rows → recharts rows + colour ════════════════════════════ */
function toPieData(rows: undefined[] | Record<string, any>) {
  return rows
    .filter(r => !!(r?.category && r?.count))
    .map((r, i) => {
      return {
        name: r.category ?? 'NA',
        value: Number(r.count ?? 0),
        fill: PIE_COLORS[i % PIE_COLORS.length],
      };
    });
}

/* ═══ Tooltip  (matches your black tooltip) ════════════════════════════ */
const DarkTooltip = ({ active, payload }: any) =>
  active && payload?.length ? (
    <div className="rounded bg-black/90 px-3 py-1 text-xs text-white">
      {payload[0].name}: {payload[0].value}
    </div>
  ) : null;

/* ═══ Component  ═══════════════════════════════════════════════════════ */
export default function PieChartsForInsights({
  loadingKeyInsights,
  leadConcerns,
  agentPitchGaps,
}: Props) {
  const left = React.useMemo(() => toPieData(leadConcerns), [leadConcerns]);
  const right = React.useMemo(
    () => toPieData(agentPitchGaps),
    [agentPitchGaps],
  );

  if (loadingKeyInsights) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-[306px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
        <Skeleton className="h-[306px] rounded-xl bg-gradient-to-r from-[#EDEDED] to-[#D7D7D7]" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {leadConcerns?.length > 0 && (
        <ChartCard title="Lead's Concerns" data={left} />
      )}

      {agentPitchGaps?.length > 0 && (
        <ChartCard title="Agent’s Sales Pitch Gaps" data={right} />
      )}
    </div>
  );
}

/* helper for each card to avoid duplication */
function ChartCard({
  title,
  data,
}: {
  title: string;
  data: { name: string; value: number; fill: string }[];
}) {
  return (
    <div className="rounded-lg border p-4">
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>

      {/* chart + legend side-by-side */}
      <div className="flex items-center justify-start gap-4 flex-wrap">
        <ResponsiveContainer width="50%" height={240}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius="90%"
              strokeOpacity={0}
              className='focus:outline-none focus:ring-0'
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.fill} className='focus:outline-none focus:ring-0' />
              ))}
            </Pie>

            {/* tooltip still on the chart canvas */}
            <Tooltip content={<DarkTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* legend column */}
        <ul className="ml-4 flex flex-col gap-1 text-sm overflow-y-auto max-h-[240px]">
          {data.map((d, i) => (
            <li key={i} className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full"
                style={{ background: d.fill }}
              />
              <span className="truncate">{d.name}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
